import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";

export interface MovingItem {
  key: string;
  label: string;
  icon: string;
  count: number;
}

interface ConfirmMergeModalProps {
  isOpen: boolean;
  merging: boolean;
  mainName?: string;
  mergeName?: string;
  items: MovingItem[];
  pseaNote?: string | null;
  flagNote?: string | null;
  aliasNote?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Final check before an irreversible merge. The user must tick an explicit
 * acknowledgement, which resets each time the dialog opens.
 */
const ConfirmMergeModal = ({
  isOpen,
  merging,
  mainName,
  mergeName,
  items,
  pseaNote,
  flagNote,
  aliasNote,
  onCancel,
  onConfirm,
}: ConfirmMergeModalProps) => {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (isOpen) setAcknowledged(false);
  }, [isOpen]);

  const moving = items.filter((item) => item.count > 0);

  return (
    <Modal
      isOpen={isOpen}
      toggle={merging ? undefined : onCancel}
      centered
      backdrop={merging ? "static" : true}
    >
      <ModalBody className="p-4">
        <div className="d-flex gap-3 mb-3">
          <div className="avatar-sm flex-shrink-0">
            <div className="avatar-title rounded-circle bg-danger-subtle text-danger fs-20">
              <i className="ri-git-merge-line"></i>
            </div>
          </div>
          <div>
            <h5 className="mb-1">Merge these contractors?</h5>
            <p className="text-ink-muted mb-0 fs-13">
              <strong className="text-body">{mergeName}</strong> will be
              permanently deleted after its records are moved to{" "}
              <strong className="text-body">{mainName}</strong>.
            </p>
          </div>
        </div>

        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fs-13 fw-semibold mb-2">
            Moving to the main contractor
          </div>
          {moving.length === 0 ? (
            <div className="fs-13 text-ink-muted">
              {flagNote || aliasNote
                ? "No related records. The flag and names below still carry over."
                : "No related records — only profile details are combined."}
            </div>
          ) : (
            <ul className="list-unstyled mb-0 fs-13 vstack gap-1">
              {moving.map((item) => (
                <li key={item.key} className="d-flex align-items-center gap-2">
                  <i className={`${item.icon} text-muted`} aria-hidden="true"></i>
                  <span className="flex-grow-1">{item.label}</span>
                  <span className="fw-semibold">
                    {item.count.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {flagNote ? (
            <div className="fs-13 mt-2 pt-2 border-top text-ink-danger">
              <i className="ri-flag-line align-bottom me-1" aria-hidden="true"></i>
              {flagNote}
            </div>
          ) : null}
          {aliasNote ? (
            <div className="fs-13 mt-2 pt-2 border-top">
              <i
                className="ri-price-tag-3-line text-ink-info align-bottom me-1"
                aria-hidden="true"
              ></i>
              {aliasNote}
            </div>
          ) : null}
          {pseaNote ? (
            <div className="fs-13 mt-2 pt-2 border-top">
              <i className="ri-shield-check-line text-warning align-bottom me-1"></i>
              {pseaNote}
            </div>
          ) : null}
        </div>

        <div className="form-check">
          <Input
            type="checkbox"
            className="form-check-input"
            id="merge-acknowledge"
            checked={acknowledged}
            disabled={merging}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <Label className="form-check-label fs-13" for="merge-acknowledge">
            I understand this cannot be undone.
          </Label>
        </div>
      </ModalBody>
      <ModalFooter className="bg-light px-4 py-3">
        <Button
          color="light"
          className="material-shadow-none"
          onClick={onCancel}
          disabled={merging}
        >
          Cancel
        </Button>
        <Button
          color="danger"
          className="material-shadow-none"
          onClick={onConfirm}
          disabled={!acknowledged || merging}
        >
          {merging ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <i className="ri-git-merge-line align-bottom me-1"></i>
          )}
          {merging ? "Merging…" : "Merge contractors"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmMergeModal;
